import { HttpClient, HttpResponse } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { BaseHrefService } from '@nibtravel/shared/util/base-href';
import { ENV_VARIABLES, IEnvironment } from '@nibtravel/shared/util/constants';
import { DomService } from '@nibtravel/shared/util/dom';
import {
  ApiUrl,
  AppConfigMap,
  defaultConfigState,
  IApiUrl,
  IAppConfig,
  Stage,
} from '@nibtravel/shared/util/models';
import { Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { SumoLoggerOptions } from 'sumo-logger';

import { EnvironmentHeader, IAppConfigService, TenantHeader } from './models';

@Injectable()
export class AppConfigService implements IAppConfigService {
  stage!: Stage | string;
  brand = '';

  constructor(
    private hrefService: BaseHrefService,
    private http: HttpClient,
    private domService: DomService,
    @Inject(ENV_VARIABLES) @Optional() private environment: IEnvironment
  ) {}

  /**
   * Public function that triggers the entire logic to load the app config.
   *
   * @returns {Promise<IAppConfig>}
   * @memberof AppConfigService
   */
  loadBrandConfig(): Observable<IAppConfig> {
    const configPath = (suffix?: string | null) =>
      `${this.hrefService.config}/config${suffix ? `.${suffix}` : ''}.json`;

    return this.http.get(configPath(), { observe: 'response' }).pipe(
      tap((res) => this.resolveFromHeaders(res)),
      switchMap((_) =>
        this.http.get<AppConfigMap>(configPath(this.stage), {
          observe: 'response',
        })
      ),
      map((res) => this.configHandler(res))
    );
  }

  /**
   * @description
   * To ease the requirements for testing, we introduced a query param where it can set the brand.
   * The functionality is only available for non-prod environments (via build environment variables).
   *
   * NOTE: This code runs before the entire angular app is initialized so we can't use the RouterModule here.
   * Current priority order to define the brand code
   *  1. Route > 2. HttpResponse header > 3. Fallback brand code from Environment Variables
   *
   */
  private configHandler(response: HttpResponse<AppConfigMap>): IAppConfig {
    const config: AppConfigMap = response.body || ({} as AppConfigMap);
    let brandConfig = config[this.brand];

    if (brandConfig != null) {
      brandConfig = {
        ...defaultConfigState,
        ...brandConfig,
        stage: this.stage,
        production: this.stage === Stage.Prod,
        logo: brandConfig.logo ?? 'logo',
        sumo: this.getSumoConfig(config.sumo, brandConfig.sumo),
        contentful: this.getContentfulConfig(
          config.contentful,
          brandConfig.contentful
        ),
      };
      return this.buildFullUrls(brandConfig);
    } else {
      return defaultConfigState;
    }
  }

  private getContentfulConfig(
    globalConfig: Record<string, unknown>,
    brandConfig?: Record<string, unknown>
  ) {
    return {
      ...(globalConfig ?? {}),
      ...(brandConfig ?? {}),
      isPreview: this.stage === Stage.Kaos,
    };
  }

  private getSumoConfig(
    globalSumoConfig: SumoLoggerOptions,
    brandSumoConfig?: SumoLoggerOptions
  ): SumoLoggerOptions {
    return {
      ...(globalSumoConfig ?? {}),
      ...(brandSumoConfig ?? {}),
    };
  }

  private buildFullUrls(brandConfig: IAppConfig): IAppConfig {
    const apiUrlInstance = (api: IApiUrl) => new ApiUrl(api.url, api.version);

    if (brandConfig) {
      Object.keys(brandConfig).forEach((key) => {
        if (brandConfig[key] && (brandConfig[key] as IApiUrl).url) {
          brandConfig[key] = apiUrlInstance(brandConfig[key] as IApiUrl);
        }
      });
    }

    return brandConfig;
  }

  private resolveFromHeaders(res: HttpResponse<unknown>): void {
    this.stage = res.headers.get(EnvironmentHeader) || Stage.Kaos;
    this.brand = res.headers.get(TenantHeader) || '';

    if (this.stage !== Stage.Prod) {
      const urlParams = new URLSearchParams(
        this.domService.getLocation()?.search
      );

      const routeBrandCode: string | null = urlParams.get('brandCode');
      if (routeBrandCode) {
        this.brand = routeBrandCode;
      }
    }

    if (!this.brand) {
      this.brand = this.environment?.fallbackBrand || '';
    }
  }
}
